import FacetTypes, { isUnknownDynamicFacet } from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import {
    filterFlatFacetOptionsOwnedByPrecedence,
    FlatFacetForCategoryResolution,
    isFacetOptionChecked,
} from '@/utils/search/searchQuery';
import { parseSearchSeoPath } from '@/utils/search/searchSeoPath';

type NestedFacetNode = FlatFacetForCategoryResolution & {
    selected?: boolean;
    seoURL?: string;
    categories?: NestedFacetNode[] | { facets?: NestedFacetNode[] };
};

const isSubjectLikeFacetId = (facetId: string): boolean => /subject/i.test(String(facetId));

const isNumericCategoryToken = (id: string | number | undefined | null): id is string | number =>
    id !== undefined && id !== null && /^\d+$/.test(String(id).trim());

const appendNumericCategoryTokens = (ids: Set<string>, values: Array<string | number | undefined | null>) => {
    for (const value of values) {
        if (isNumericCategoryToken(value)) {
            ids.add(String(value).trim());
        }
    }
};

/** Flat browse dimensions intentionally folded into saved-search `category_id` on `/search/`. */
const SAVE_SEARCH_FLAT_FACET_IDS = new Set<string>([
    FacetTypes.CREATOR_ID,
    FacetTypes.STYLE_PERIOD_ID,
    FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
    FacetTypes.ART_SUBJECT_ID,
]);

const isSaveSearchFlatFacet = (facet: FlatFacetForCategoryResolution): boolean =>
    Boolean(facet.options?.length) && SAVE_SEARCH_FLAT_FACET_IDS.has(facet.id);

const isSubjectFilterAppliedInUrl = (pathname: string, queryParams: Record<string, unknown>): boolean => {
    if (parseSearchSeoPath(pathname)?.subjectValueSlug) {
        return true;
    }
    return Object.entries(queryParams).some(([key, value]) => isSubjectLikeFacetId(key) && Boolean(value));
};

const walkNestedFacetNodes = (root: NestedFacetNode | undefined, visit: (node: NestedFacetNode) => void) => {
    if (!root) {
        return;
    }
    visit(root);
    const rawCategories = root.categories;
    const children: NestedFacetNode[] = Array.isArray(rawCategories) ? rawCategories : (rawCategories?.facets ?? []);
    children.forEach((child) => walkNestedFacetNodes(child, visit));
};

/**
 * Collects numeric taxonomy option ids for saved search `category_id`, matching live search filters on `/search/`.
 * Semantic URL params (`creator`, `style`, `material`, `origin`, dynamic `subject`, etc.) are resolved via the same
 * rules as filter chips (`isFacetOptionChecked`), without adding those keys to the save-search whitelist.
 */
export const collectSaveSearchCategoryIds = (
    queryParams: Record<string, unknown>,
    pathname: string,
    rawFacets: FlatFacetForCategoryResolution[]
): number[] => {
    const ids = new Set<string>();

    const categoryParam = queryParams[paramTypes.CATEGORY_ID];
    if (categoryParam !== undefined && categoryParam !== null && categoryParam !== '') {
        String(categoryParam)
            .split(',')
            .map((segment) => segment.trim())
            .filter((segment) => /^\d+$/.test(segment))
            .forEach((segment) => ids.add(segment));
    }

    const recordOption = (facetId: string, option: NestedFacetNode) => {
        if (isSubjectLikeFacetId(facetId) && !isSubjectFilterAppliedInUrl(pathname, queryParams)) {
            return;
        }
        if (!isFacetOptionChecked(pathname, queryParams, facetId, option, Boolean(option.selected), rawFacets)) {
            return;
        }
        appendNumericCategoryTokens(ids, [option.id]);
    };

    for (const facet of rawFacets) {
        if (facet.id === FacetTypes.ORIGIN_ID) {
            walkNestedFacetNodes(facet as NestedFacetNode, (node) => recordOption(FacetTypes.ORIGIN_ID, node));
            continue;
        }

        if (!isSaveSearchFlatFacet(facet) && !isSubjectLikeFacetId(facet.id) && !isUnknownDynamicFacet(facet.id)) {
            continue;
        }

        if (!facet.options?.length) {
            continue;
        }

        const options = filterFlatFacetOptionsOwnedByPrecedence(facet.options, rawFacets, facet.id);
        options.forEach((option) => recordOption(facet.id, option as NestedFacetNode));
    }

    return [...ids].map((id) => Number(id));
};
